/**
 * 設定 → 端数処理（#108、docs/41。2026-10-01 射場さんの決定 → #156、docs/65 §3・★1 a、Figma「設定の残り（10/9）」⑰で iOS 風に）
 *   1. 明細と率の行の金額（数量×単価・基礎×率の1円未満）：切り捨て／四捨五入／切り上げ。既定は四捨五入（★1：率の行は明細と同じ）
 *   2. 消費税（税率ごとの合計に1回）：既定は切り捨て。顧客ごとに変えるときは顧客の画面の「請求」（★4）
 *   3. マイナスの金額：絶対値を基準（既定、Excel と同じ）／数値を基準（★2：Board と同じ2択）
 *   保存すると、これから作る見積から効く。作りかけの見積は自動では変えない（★3。見積の画面の「既定値で再計算」）。
 *   出した見積・請求書は変わらない。変えられるのは管理者だけ（会社の設定）
 * #156：節「1円未満の端数」（明細・率の行／消費税）・「マイナスの金額」（基準）。どの行も名前の下に全幅のセグメント。
 *   選んだ瞬間に PUT /company/rounding（送るのは全部の値＝今と同じ形）。失敗したら元に戻してトースト。「端数処理を保存する」ボタンはやめた
 */
import {
  NEGATIVE_ROUNDING_EXAMPLE,
  NEGATIVE_ROUNDING_LABEL,
  NegativeRounding,
  REST_MSG,
  ROUNDING_LABEL,
  ROUNDING_MSG,
  RoundingDirection,
  SETTINGS_MSG,
  type RoundingSettings,
} from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId } from "react";
import { api, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { GroupList, GroupSection, GroupValueRow } from "../components/GroupList.jsx";
import { Segmented } from "../components/Segmented.jsx";
import { SettingsHead, useRestPageClass } from "../components/SettingsHead.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { t } from "../labels.js";

export const ROUNDING_KEY = ["company-rounding"] as const;

export function SettingsRoundingPage() {
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const q = useQuery({ queryKey: ROUNDING_KEY, queryFn: () => api<RoundingSettings>("GET", "/company/rounding") });
  const back = useBackNav({ to: "/settings", label: "設定" });
  usePageName(REST_MSG.roundingTitle);
  const cls = useRestPageClass("roundpage");
  const lineId = useId();
  const taxId = useId();
  const negId = useId();

  const save = useMutation({
    mutationFn: (v: RoundingSettings) => api<RoundingSettings>("PUT", "/company/rounding", v),
    // 押した瞬間に見た目を変える（失敗したら元に戻す）
    onMutate: (v) => {
      const prev = qc.getQueryData<RoundingSettings>(ROUNDING_KEY);
      qc.setQueryData(ROUNDING_KEY, v);
      return { prev };
    },
    onSuccess: (v) => {
      qc.setQueryData(ROUNDING_KEY, v);
      // 作りかけの見積の「既定値で再計算」を出し直す
      qc.removeQueries({ queryKey: ["estimate"] });
      void qc.invalidateQueries({ queryKey: ["partner"] });
      toast(ROUNDING_MSG.saved);
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(ROUNDING_KEY, ctx.prev);
      toast(messageOf(e), undefined, "error");
    },
  });

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data) return <p className="hint">読み込んでいます…</p>;
  const cur = q.data;
  const pick = <K extends keyof RoundingSettings>(key: K, v: RoundingSettings[K]) => {
    if (cur[key] === v || save.isPending) return;
    save.mutate({ ...cur, [key]: v });
  };
  /** 名前の下に全幅のセグメント（Figma ⑰） */
  const segRow = <K extends keyof RoundingSettings>(key: K, title: string, titleId: string, options: readonly RoundingSettings[K][], text: (o: RoundingSettings[K]) => string) => (
    <GroupValueRow
      title={title}
      titleId={titleId}
      className="grow-segwide"
      value={
        <Segmented
          options={options.map((o) => ({ value: o, label: text(o), testId: `rounding-${key}-${o}` }))}
          value={cur[key]}
          onChange={(o) => pick(key, o)}
          labelledBy={titleId}
          disabled={!admin}
          testId={`rounding-${key}`}
          className="is-wide"
        />
      }
    />
  );

  return (
    <div className={cls} data-testid="rounding-settings">
      <SettingsHead back={back} title={REST_MSG.roundingTitle} />
      {!admin ? (
        <p className="est-note" id="roundingMemberNote">
          {SETTINGS_MSG.memberNote}
        </p>
      ) : null}
      <GroupSection
        title={REST_MSG.roundingSectionYen}
        help={
          <Help
            testId="help-rounding-line"
            text={
              <>
                <span className="helpp">
                  <b>{REST_MSG.roundingLine}</b>
                  {t("　数量×単価、率の行（{pw:rateExample}）の1円未満。既定：四捨五入")}
                </span>
                <span className="helpp">
                  <b>{REST_MSG.roundingTax}</b>
                  　税率ごとの合計に1回だけ適用（インボイス制度）。既定：切り捨て。顧客ごとの変更は顧客画面の「請求」から
                </span>
              </>
            }
          />
        }
        note={REST_MSG.roundingTaxNote}
        data-testid="rounding-sec-yen"
      >
        <GroupList plain>
          {segRow("lineRounding", REST_MSG.roundingLine, lineId, RoundingDirection.options, (o) => ROUNDING_LABEL[o])}
          {segRow("taxRounding", REST_MSG.roundingTax, taxId, RoundingDirection.options, (o) => ROUNDING_LABEL[o])}
        </GroupList>
      </GroupSection>
      <GroupSection
        title={REST_MSG.roundingSectionNegative}
        help={<Help testId="help-rounding-negative" text="値引きなどマイナス金額の扱い。明細・率の行・消費税に適用。既定：絶対値基準" />}
        note={<span data-testid="rounding-negative-example">{NEGATIVE_ROUNDING_EXAMPLE[cur.negativeRounding]}</span>}
        data-testid="rounding-sec-negative"
      >
        <GroupList plain>{segRow("negativeRounding", REST_MSG.roundingNegative, negId, NegativeRounding.options, (o) => NEGATIVE_ROUNDING_LABEL[o])}</GroupList>
      </GroupSection>
      <p className="rest-foot" data-testid="rounding-foot">
        {REST_MSG.roundingFootNote}
      </p>
      {node}
    </div>
  );
}
